import React, { useEffect, useMemo, useState } from "react";
import { api, formatIDR, formatDate } from "@/lib/api";
import { toast } from "sonner";
import { AlertTriangle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export default function Mitras() {
  const [tenants, setTenants] = useState([]);
  const [deletingId, setDeletingId] = useState(null);
  const [plans, setPlans] = useState([]);
  const [open, setOpen] = useState(false);
  const [target, setTarget] = useState(null);
  const [form, setForm] = useState({ plan_id: "", cycle: "monthly", months: 1, note: "" });

  const load = async () => {
    try {
      const [tenantRes, planRes] = await Promise.all([api.get("/admin/tenants"), api.get("/plans")]);
      setTenants(tenantRes.data || []);
      setPlans(planRes.data || []);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Gagal memuat mitra");
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedPlan = useMemo(
    () => plans.find((plan) => plan.id === form.plan_id),
    [plans, form.plan_id]
  );
  const incompatible = Boolean(target && selectedPlan && (
    Number(target.outlets_count || 0) > Number(selectedPlan.max_outlets || 0)
    || Number(target.users_count || 0) > Number(selectedPlan.max_users || 0)
  ));

  const activate = async () => {
    if (!form.plan_id) return toast.error("Pilih paket");
    if (incompatible) return toast.error("Pemakaian mitra melebihi limit paket yang dipilih");
    try {
      await api.post("/admin/subscriptions/activate", {
        tenant_id: target.id,
        ...form,
        months: Number(form.months),
      });
      toast.success("Subscription aktif");
      setOpen(false);
      await load();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Subscription gagal diaktifkan");
    }
  };

  const toggle = async (tenantId) => {
    try {
      await api.post(`/admin/tenants/${tenantId}/toggle`);
      await load();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Status mitra gagal diubah");
    }
  };

  const removePending = async (tenant) => {
    if (!window.confirm(`Hapus pendaftaran ${tenant.name}? Mitra akan hilang dari daftar dan tidak dapat login. Data tetap disimpan untuk audit.`)) return;
    setDeletingId(tenant.id);
    try {
      await api.delete(`/admin/tenants/${tenant.id}`);
      toast.success("Pendaftaran mitra dihapus");
      await load();
    } catch (error) { toast.error(error?.response?.data?.detail || "Gagal menghapus mitra"); }
    finally { setDeletingId(null); }
  };

  return (
    <div className="p-6 lg:p-10">
      <div className="mb-6">
        <div className="overline text-zinc-500">DAFTAR MITRA</div>
        <h1 className="font-display font-black text-4xl tracking-tighter">Mitra & Subscription</h1>
        <p className="text-sm text-zinc-500 mt-2">Pemakaian outlet dan pengguna dibandingkan langsung dengan limit paket aktif.</p>
      </div>
      <div className="border border-border overflow-x-auto">
        <table className="w-full text-sm min-w-[1050px]">
          <thead className="bg-zinc-50 border-b border-border"><tr>
            <th className="text-left p-3 overline">Mitra</th>
              <th className="text-left p-3 overline">Admin Mitra</th>
            <th className="text-left p-3 overline">Plan</th>
            <th className="text-left p-3 overline">Status</th>
            <th className="text-left p-3 overline">Outlet</th>
            <th className="text-left p-3 overline">User Aktif</th>
            <th className="text-left p-3 overline">Fitur</th>
            <th className="text-left p-3 overline">Mulai</th>
            <th className="text-left p-3 overline">Berakhir</th>
            <th className="w-[144px] min-w-[144px] p-3 text-center overline">Aksi</th>
          </tr></thead>
          <tbody>
            {tenants.length === 0 && <tr><td colSpan={10} className="p-8 text-center text-zinc-500">Belum ada mitra terdaftar</td></tr>}
            {tenants.map((tenant) => {
              const limits = tenant.entitlements || {};
              const outletOver = Number(tenant.outlets_count || 0) > Number(limits.max_outlets || 0);
              const userOver = Number(tenant.users_count || 0) > Number(limits.max_users || 0);
              return (
                <tr key={tenant.id} className={`border-b border-border transition-colors hover:bg-slate-50/80 ${limits.over_limit ? "bg-rose-50" : ""}`}>
                  <td className="p-3"><div className="font-semibold">{tenant.name}</div><div className="text-xs text-zinc-500 font-mono">{tenant.id.slice(0, 8)}</div></td>
                  <td className="p-3"><div className="text-xs font-semibold text-zinc-800">{tenant.admin_name || "Admin"}</div><div className="mt-1 text-xs text-blue-700 break-all">{tenant.admin_email || "Email belum tersedia"}</div>{tenant.admin_phone && <div className="mt-1 text-[11px] text-zinc-400">{tenant.admin_phone}</div>}</td>
                  <td className="p-3"><div>{tenant.plan?.name || limits.plan_name || "—"}</div>{tenant.plan && !tenant.plan.active && <div className="text-[10px] text-amber-700">Paket tidak dijual</div>}</td>
                  <td className="p-3"><span className={`text-xs uppercase tracking-wider font-semibold px-2 py-0.5 ${tenant.subscription?.status === "active" ? "bg-emerald-100 text-emerald-800" : tenant.subscription?.status === "trial" ? "bg-blue-100 text-blue-800" : "bg-zinc-100 text-zinc-600"}`}>{tenant.subscription?.status || "-"}</span></td>
                  <td className={`p-3 tabular-nums font-semibold ${outletOver ? "text-rose-700" : ""}`}>{tenant.outlets_count}/{limits.max_outlets ?? "-"}</td>
                  <td className={`p-3 tabular-nums font-semibold ${userOver ? "text-rose-700" : ""}`}>{tenant.users_count}/{limits.max_users ?? "-"}</td>
                  <td className="p-3 tabular-nums">{(limits.feature_keys || []).length}</td>
                  <td className="p-3 text-xs text-zinc-500">{formatDate(tenant.subscription?.start_at)}</td>
                  <td className="p-3 text-xs text-zinc-500">{formatDate(tenant.subscription?.end_at)}</td>
                                    <td className="p-3 align-middle">
                    {/* KRAYA_MITRAS_ACTION_UI_V1 */}
                    <div className="mx-auto flex w-[116px] flex-col gap-2">
                      <button
                        onClick={() => {
                          setTarget(tenant);
                          setForm({ plan_id: tenant.plan?.id || "", cycle: "monthly", months: 1, note: "" });
                          setOpen(true);
                        }}
                        data-testid={`mitra-activate-${tenant.id}`}
                        className="w-full whitespace-nowrap rounded-xl bg-slate-950 px-3 py-2.5 text-xs font-bold text-white shadow-sm transition hover:-translate-y-0.5 hover:bg-blue-600 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                      >
                        Atur Paket
                      </button>
                      {tenant.subscription?.status === "pending_verification" && <button disabled={!!deletingId} onClick={() => removePending(tenant)} className="rounded-xl border border-rose-200 px-3 py-2 text-xs font-bold text-rose-700 disabled:opacity-50">{deletingId === tenant.id ? "Menghapus..." : "Hapus Mitra"}</button>}
                      <button
                        onClick={() => toggle(tenant.id)}
                        data-testid={`mitra-toggle-${tenant.id}`}
                        className={`w-full whitespace-nowrap rounded-xl border px-3 py-2.5 text-xs font-bold transition focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                          tenant.active === false
                            ? "border-emerald-200 bg-emerald-50 text-emerald-700 hover:border-emerald-300 hover:bg-emerald-100 focus:ring-emerald-500"
                            : "border-rose-200 bg-rose-50 text-rose-700 hover:border-rose-300 hover:bg-rose-100 focus:ring-rose-500"
                        }`}
                      >
                        {tenant.active === false ? "Aktifkan" : "Nonaktifkan"}
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none max-w-xl">
          <DialogHeader><DialogTitle>Aktifkan Subscription</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="border border-border p-3 text-sm">
              <div>Mitra: <strong>{target?.name}</strong></div>
              <div className="text-xs text-zinc-500 mt-1 tabular-nums">Pemakaian saat ini: {target?.outlets_count || 0} outlet · {target?.users_count || 0} pengguna aktif</div>
            </div>
            <div><label className="overline">Plan</label>
              <select data-testid="mitra-plan-select" value={form.plan_id} onChange={(event) => setForm({ ...form, plan_id: event.target.value })} className="w-full border border-border px-3 py-2 mt-1">
                <option value="">— pilih —</option>
                {plans.filter((plan) => plan.active || plan.id === target?.plan?.id).map((plan) => {
                  const invalidForTenant = Number(target?.outlets_count || 0) > Number(plan.max_outlets || 0) || Number(target?.users_count || 0) > Number(plan.max_users || 0);
                  return <option key={plan.id} value={plan.id} disabled={invalidForTenant}>{plan.name} — {formatIDR(plan.price_monthly)}/bln · {plan.max_outlets} outlet · {plan.max_users} user{invalidForTenant ? " (pemakaian melebihi limit)" : ""}</option>;
                })}
              </select>
            </div>

            {selectedPlan && (
              <div className={`border p-4 ${incompatible ? "border-rose-300 bg-rose-50" : "border-border bg-zinc-50"}`}>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><span className="block overline text-zinc-500">LIMIT OUTLET</span><strong className="tabular-nums">{target?.outlets_count || 0}/{selectedPlan.max_outlets}</strong></div>
                  <div><span className="block overline text-zinc-500">LIMIT USER</span><strong className="tabular-nums">{target?.users_count || 0}/{selectedPlan.max_users}</strong></div>
                </div>
                <div className="mt-3 text-xs text-zinc-600">{(selectedPlan.feature_details || []).length} fitur: {(selectedPlan.feature_details || []).map((feature) => feature.label).join(", ") || "tidak ada fitur operasional"}</div>
                {incompatible && <div className="mt-3 flex gap-2 text-sm text-rose-800"><AlertTriangle className="h-4 w-4 shrink-0" /> Nonaktifkan outlet/pengguna terlebih dahulu atau pilih paket dengan limit lebih besar.</div>}
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div><label className="overline">Siklus</label>
                <select data-testid="mitra-cycle-select" value={form.cycle} onChange={(event) => setForm({ ...form, cycle: event.target.value })} className="w-full border border-border px-3 py-2 mt-1">
                  <option value="monthly">Bulanan</option><option value="yearly">Tahunan</option>
                </select>
              </div>
              <div><label className="overline">Jumlah Periode</label>
                <input data-testid="mitra-months-input" min="1" type="number" value={form.months} onChange={(event) => setForm({ ...form, months: event.target.value })} className="w-full border border-border px-3 py-2 mt-1 font-mono-num" />
              </div>
            </div>
            <div><label className="overline">Catatan</label><input data-testid="mitra-note-input" value={form.note} onChange={(event) => setForm({ ...form, note: event.target.value })} className="w-full border border-border px-3 py-2 mt-1" placeholder="Bukti transfer, perubahan paket, dll." /></div>
          </div>
          <div className="flex gap-2 mt-4">
            <button onClick={() => setOpen(false)} className="flex-1 py-3 border border-border">Batal</button>
            <button onClick={activate} disabled={!form.plan_id || incompatible} data-testid="mitra-activate-confirm" className="flex-1 py-3 bg-[#002FA7] text-white font-semibold disabled:opacity-40 disabled:cursor-not-allowed">Aktifkan</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
